import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { getPostOnce } from '../../services/firebase/feed.service';
import { getDbPathForPost, postTypeNeedsOwner } from '../../services/firebase/feed-utils';
import { subscribeToPublicFeedItems } from '../../services/firebase/publicFeed.service';
import { FeedPostType, DEFAULT_FEED_FILTER } from '../../types/feed';
import { RootState } from '../index';
import { selectShadowbannedUidsSet } from './shadowbanSlice';
import { selectNonPublicUidsSet } from './privacySlice';
import logger from '../../utils/logger';

// Define types for our slice state
interface PublicFeedState {
  feedItemsList: string[];  // Ordered list of feed item IDs
  feedItemsData: Record<string, any>;  // Feed item metadata
  posts: Record<string, any>;  // The actual content of posts
  isLoading: boolean;
  isLoadingMore: boolean;  // QM5: separate from isLoading so "Load More" doesn't flash the full-page spinner
  error: string | null;
  fetchedBatchesCount: number;
  hasPostDataBeenFetched: boolean;
}

// Initial state
const initialState: PublicFeedState = {
  feedItemsList: [],
  feedItemsData: {},
  posts: {},
  isLoading: false,
  isLoadingMore: false,
  error: null,
  fetchedBatchesCount: 1,
  hasPostDataBeenFetched: false
};

// Batch size for feed items
const BATCH_SIZE = 10;

// Async thunks
export const fetchPublicFeedItems = createAsyncThunk(
  'publicFeed/fetchItems',
  async (arg: { batchCount?: number } | undefined, { dispatch, getState, rejectWithValue }) => {
    return new Promise<{ feedItemsList: string[], feedItemsData: Record<string, any> }>((resolve, reject) => {
      try {
        // Check if authentication is initialized
        const fullState = getState() as { publicFeed: PublicFeedState; auth: { isInitialized: boolean; isAuthenticated: boolean } };
        if (!fullState.auth.isInitialized) {
          logger.warn('Cannot fetch public feed: Authentication not initialized');
          return reject(rejectWithValue('Authentication not initialized'));
        }

        // Calculate wanted items count based on batch size. "Load More"
        // passes its target batch count explicitly: the store counter is
        // only bumped in fetchNextPublicBatch.fulfilled, so reading it here
        // made the first Load More refetch the identical window
        const state = fullState.publicFeed;
        const batchCount = arg?.batchCount ?? state.fetchedBatchesCount;
        const wantedItemsCount = batchCount * BATCH_SIZE;
        
        // Unsubscribe function (will be called after initial data fetch)
        let unsubscribe: () => void;
        let hasResolved = false;
        
        // Callback to process feed items
        const handleFeedItems = (feedItemsList: string[], feedItemsData: any) => {
          
          // Prevent duplicate processing
          if (hasResolved) {
            logger.info('Feed items already processed, skipping duplicate');
            return;
          }
          
          // Get current posts
          const { posts } = (getState() as { publicFeed: PublicFeedState }).publicFeed;
          
          // Find which items we need to fetch full data for
          const unfetchedItems = feedItemsList.filter(key => !posts[key]);
          
          // Fetch data for each unfetched post IMMEDIATELY
          if (unfetchedItems.length > 0) {
            logger.info(`Fetching ${unfetchedItems.length} post data immediately`);
            dispatch(fetchPublicPostsData({ unfetchedItems, feedItemsData }));
          }
          
          // Unsubscribe after initial fetch
          if (unsubscribe) {
            unsubscribe();
          }
          
          // Mark as resolved
          hasResolved = true;
          
          // Resolve the promise with the feed data
          resolve({ feedItemsList, feedItemsData });
        };
        
        // Start the feed items fetch
        unsubscribe = subscribeToPublicFeedItems(handleFeedItems, wantedItemsCount);
      } catch (error) {
        logger.error('Error in fetchPublicFeedItems:', error);
        reject(error);
      }
    });
  }
);

export const fetchNextPublicBatch = createAsyncThunk(
  'publicFeed/fetchNextBatch',
  async (_, { dispatch, getState }) => {
    // Increase the batch count
    const state = getState() as { publicFeed: PublicFeedState };
    const nextBatchCount = state.publicFeed.fetchedBatchesCount + 1;

    // Load with increased batch count
    const result = await dispatch(fetchPublicFeedItems({ batchCount: nextBatchCount })).unwrap();
    
    return {
      ...result,
      batchNumber: nextBatchCount
    };
  }
);

export const fetchPublicPostsData = createAsyncThunk(
  'publicFeed/fetchPostsData',
  async ({ unfetchedItems, feedItemsData }: {
    unfetchedItems: string[],
    feedItemsData: Record<string, any>
  }) => {
    const postsData: Record<string, any> = {};

    const fetchPromises = unfetchedItems.map(async (key) => {
      const feedItem = feedItemsData[key];

      if (!feedItem) {
        logger.warn(`Public feed item ${key} has no data`);
        postsData[key] = null;
        return;
      }

      const type = feedItem.postType || feedItem.type;

      if (!type && !feedItem.itemRef) {
        logger.warn(`Public feed item ${key} has no type or postType:`, feedItem);
        postsData[key] = null;
        return;
      }

      const dbPath = getDbPathForPost(feedItem, key);
      if (!dbPath) {
        // Unknown type - extract available content as fallback
        logger.warn(`Unknown public feed item type: ${type}. Item:`, feedItem);
        const content = feedItem.content || feedItem.note || feedItem.text || feedItem.description || feedItem.title || `Content of type ${type}`;
        postsData[key] = {
          key,
          content,
          note: feedItem.note || '',
          text: feedItem.text || '',
          title: feedItem.title || '',
          description: feedItem.description || '',
          type,
          postType: type,
          dateCreated: feedItem.dateCreated,
          owner: feedItem.owner || ''
        };
        return;
      }

      const postData = await getPostOnce(dbPath);
      const owner = feedItem.owner || '';
      // Inject owner for post types that don't store it in the post data
      postsData[key] = (owner && postTypeNeedsOwner(type || ''))
        ? { ...postData, owner }
        : postData;
    });

    await Promise.all(fetchPromises);
    return postsData;
  }
);

// Create the slice
const publicFeedSlice = createSlice({
  name: 'publicFeed',
  initialState,
  reducers: {
    resetPublicFeed: () => initialState,
    clearPublicFeedError: (state) => {
      state.error = null;
    },
    clearPublicFeedLoading: (state) => {
      state.isLoading = false;
    },
    // Update like status for a post - keeps feed in sync when navigating back
    updatePublicPostLikeStatus: (state, action) => {
      const { postId, userId, liked } = action.payload;

      // Update in feedItemsData
      if (state.feedItemsData[postId]) {
        if (!state.feedItemsData[postId].likes) {
          state.feedItemsData[postId].likes = {};
        }
        if (liked) {
          state.feedItemsData[postId].likes[userId] = true;
          const currentCount = state.feedItemsData[postId].likesCount || 0;
          state.feedItemsData[postId].likesCount = currentCount + 1;
        } else {
          delete state.feedItemsData[postId].likes[userId];
          const currentCount = state.feedItemsData[postId].likesCount || 0;
          state.feedItemsData[postId].likesCount = Math.max(0, currentCount - 1);
        }
        state.feedItemsData[postId].hasLiked = liked;
      }

      // Update in posts
      if (state.posts[postId]) {
        if (!state.posts[postId].likes) {
          state.posts[postId].likes = {};
        }
        if (liked) {
          state.posts[postId].likes[userId] = true;
          const currentCount = state.posts[postId].likesCount || 0;
          state.posts[postId].likesCount = currentCount + 1;
        } else {
          delete state.posts[postId].likes[userId];
          const currentCount = state.posts[postId].likesCount || 0;
          state.posts[postId].likesCount = Math.max(0, currentCount - 1);
        }
        state.posts[postId].hasLiked = liked;
      }
    },
    // Update comment count for a post - keeps feed in sync when navigating back
    updatePublicPostCommentCount: (state, action) => {
      const { postId, delta } = action.payload; // delta: +1 for add, -1 for remove

      // Update in feedItemsData
      if (state.feedItemsData[postId]) {
        const currentCount = state.feedItemsData[postId].commentsCount || state.feedItemsData[postId].commentCount || 0;
        state.feedItemsData[postId].commentsCount = Math.max(0, currentCount + delta);
      }

      // Update in posts
      if (state.posts[postId]) {
        const currentCount = state.posts[postId].commentsCount || state.posts[postId].commentCount || 0;
        state.posts[postId].commentsCount = Math.max(0, currentCount + delta);
      }
    },
  },
  extraReducers: (builder) => {
    // Handle fetchPublicFeedItems
    builder
      .addCase(fetchPublicFeedItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPublicFeedItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.feedItemsList = action.payload.feedItemsList;
        state.feedItemsData = action.payload.feedItemsData;
      })
      .addCase(fetchPublicFeedItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch public feed items';
      })

    // Handle fetchNextPublicBatch — QM5: use isLoadingMore, NOT isLoading, so
    // "Load More" doesn't trip the full-page spinner + empty-state path.
      .addCase(fetchNextPublicBatch.pending, (state) => {
        state.isLoadingMore = true;
        state.error = null;
      })
      .addCase(fetchNextPublicBatch.fulfilled, (state, action) => {
        state.isLoadingMore = false;
        // Update batch count
        state.fetchedBatchesCount = action.payload.batchNumber;
      })
      .addCase(fetchNextPublicBatch.rejected, (state, action) => {
        state.isLoadingMore = false;
        state.error = action.error.message || 'Failed to fetch next batch of public feed';
      })

    // Handle fetchPublicPostsData
      .addCase(fetchPublicPostsData.pending, (state) => {
        state.hasPostDataBeenFetched = false;
      })
      .addCase(fetchPublicPostsData.fulfilled, (state, action) => {
        state.posts = { ...state.posts, ...action.payload };
        state.hasPostDataBeenFetched = true;
      });
  },
});

// Export actions and reducer
export const {
  resetPublicFeed,
  clearPublicFeedError,
  clearPublicFeedLoading,
  updatePublicPostLikeStatus,
  updatePublicPostCommentCount
} = publicFeedSlice.actions;
export default publicFeedSlice.reducer;

// Selectors
export const selectPublicFeedItems = (state: { publicFeed: PublicFeedState }) => state.publicFeed.feedItemsList;
export const selectPublicFeedItemsData = (state: { publicFeed: PublicFeedState }) => state.publicFeed.feedItemsData;
export const selectPublicPosts = (state: { publicFeed: PublicFeedState }) => state.publicFeed.posts;
export const selectPublicFeedIsLoading = (state: { publicFeed: PublicFeedState }) => state.publicFeed.isLoading;
export const selectPublicFeedIsLoadingMore = (state: { publicFeed: PublicFeedState }) => state.publicFeed.isLoadingMore;
export const selectPublicFeedError = (state: { publicFeed: PublicFeedState }) => state.publicFeed.error;
export const selectPublicFeedBatchesCount = (state: { publicFeed: PublicFeedState }) => state.publicFeed.fetchedBatchesCount;
export const selectHasPublicPostDataBeenFetched = (state: { publicFeed: PublicFeedState }) => state.publicFeed.hasPostDataBeenFetched;

// Input selectors for memoized selector
const selectPublicFeedItemsList = (state: RootState) => state.publicFeed.feedItemsList;
const selectPublicFeedItemsDataObj = (state: RootState) => state.publicFeed.feedItemsData;
const selectPublicPostsObj = (state: RootState) => state.publicFeed.posts;
const selectViewedProfiles = (state: RootState) => state.user?.viewedProfiles || {};
const selectCurrentUserId = (state: RootState) => state.auth?.user?.uid || '';
const selectIsCurrentUserAdmin = (state: RootState) => state.user?.currentProfile?.isAdmin || false;
// Feed filter selector - returns enabled post types (defaults if filter not loaded yet)
const selectEnabledPostTypes = (state: RootState): FeedPostType[] =>
  state.feedFilter?.enabledTypes || DEFAULT_FEED_FILTER;

// Memoized Set for O(1) post type lookups - prevents recreating Set on every selector run
const selectEnabledPostTypesSet = createSelector(
  [selectEnabledPostTypes],
  (types): Set<string> => new Set(types)
);

// Custom selector to get combined feed data ready for display
// This is now a memoized selector that will only recalculate when inputs change
export const selectPublicFeedItemsWithData = createSelector(
  [selectPublicFeedItemsList, selectPublicFeedItemsDataObj, selectPublicPostsObj, selectViewedProfiles, selectCurrentUserId, selectShadowbannedUidsSet, selectIsCurrentUserAdmin, selectEnabledPostTypesSet, selectNonPublicUidsSet],
  (feedItemsList, feedItemsData, posts, viewedProfiles, currentUserId, shadowbannedUidsSet, isCurrentUserAdmin, enabledTypesSet, nonPublicUidsSet) => {

    // This is the EXACT pattern used in the original app's FeedFriendsScreen component
    // First map feed item keys to post data, then filter out any missing posts
    const postsList = feedItemsList
      .map(itemId => {
        // Get the feed item data and post data
        const feedItem = feedItemsData[itemId];
        const post = posts[itemId];

        // Skip items with missing data
        if (!feedItem || !post) {
          return null;
        }

        // SHADOWBAN FILTERING:
        // - Non-admin users: Hide posts from shadowbanned users (unless it's their own post)
        // - Admin users: Show all posts (with indicator added later)
        // - Users always see their own posts regardless of shadowban status
        const postOwnerId = feedItem.owner || post.owner;
        const isShadowbanned = shadowbannedUidsSet.has(postOwnerId);
        const isOwnPost = postOwnerId === currentUserId;

        if (isShadowbanned && !isCurrentUserAdmin && !isOwnPost) {
          // Non-admin viewing a shadowbanned user's post - hide it
          return null;
        }

        // PRIVACY FILTERING:
        // Hide posts from users with non-public profiles (unless own post or admin)
        const isNonPublic = nonPublicUidsSet.has(postOwnerId);
        if (isNonPublic && !isCurrentUserAdmin && !isOwnPost) {
          return null;
        }

        // POST TYPE FILTERING:
        // Filter out posts that don't match the enabled post types
        const postType = feedItem.postType || post.postType || post.type;
        if (postType && !enabledTypesSet.has(postType as FeedPostType)) {
          return null;
        }

        // Process post data

        // Make sure the author information is properly extracted from feed items
        // First try to get the data from viewed profiles if available
        // This is critical for the public feed where we might not have friend profiles
        const ownerProfile = feedItem.owner && viewedProfiles[feedItem.owner];
      
      // If owner is in viewed profiles, use that data first
      const authorDisplayName = 
        (ownerProfile?.displayName || 
         ownerProfile?.username ||
         `${ownerProfile?.firstName || ''} ${ownerProfile?.lastName || ''}`.trim()) ||
        feedItem.author?.displayName || 
        post.authorDisplayName ||
        post.displayName || 
        'Unknown User';
        
      const authorUsername = 
        ownerProfile?.username ||
        feedItem.author?.username || 
        post.username || 
        '';
        
      const authorPhotoURL = 
        ownerProfile?.profilePhotoURL || ownerProfile?.photoURL ||
        feedItem.author?.profilePhotoURL || feedItem.author?.photoURL || 
        post.profilePhotoURL || post.photoURL || 
        null;
        
      const isInstructor = ownerProfile?.isInstructor || false;
      
      // Get the likes object from either feedItem or post, ensuring it's actually an object
      let likes: Record<string, any> = {};
      if (feedItem.likes && typeof feedItem.likes === 'object') {
        likes = feedItem.likes as Record<string, any>;
      } else if (post.likes && typeof post.likes === 'object') {
        likes = post.likes as Record<string, any>;
      }
        
      // Combine with likedBy formats as well
      let likedBy: Record<string, any> = {};
      if (feedItem.likedBy && typeof feedItem.likedBy === 'object') {
        likedBy = feedItem.likedBy as Record<string, any>;
      } else if (post.likedBy && typeof post.likedBy === 'object') {
        likedBy = post.likedBy as Record<string, any>;
      }
        
      // Count likes from the objects - this is the source of truth
      // The likesCount field is NOT maintained by Cloud Functions for most post types,
      // so we should always calculate from the likes object when available
      const likesFromObject = Object.keys(likes).length;
      const likesFromLikedBy = Object.keys(likedBy).length;

      // PRIORITY: likes object > likedBy object > explicit count field
      // This ensures we always show the accurate count from the source of truth
      const explicitLikeCount = feedItem.likeCount || feedItem.likesCount || post.likeCount || post.likesCount;
      const likeCount = likesFromObject > 0 ? likesFromObject :
                        (likesFromLikedBy > 0 ? likesFromLikedBy :
                        (typeof explicitLikeCount === 'number' ? explicitLikeCount : 0));
        
      const comments = feedItem.comments || post.comments || {};
      const commentCount = feedItem.commentCount || feedItem.commentsCount || post.commentCount || post.commentsCount || 
                          (comments && typeof comments === 'object' ? Object.keys(comments).length : 0);
                            
      // Determine if the current user has liked this post - check all formats
      // This is a critical calculation since it affects the UI display
      let hasLiked = false;
      // currentUserId is already defined at the top of the selector

      if (currentUserId) {
        // Check both likes and likedBy objects
        if (likesFromObject > 0 && likes[currentUserId]) {
          hasLiked = true;
        } else if (likesFromObject > 0 && likes[`${currentUserId}`]) {
          hasLiked = true;
        } else if (likesFromLikedBy > 0 && likedBy[currentUserId]) {
          hasLiked = true;
        } else if (likesFromLikedBy > 0 && likedBy[`${currentUserId}`]) {
          hasLiked = true;
        }
      }

      // Convert photos from Record<string, Photo> to Array<Photo> for feed display
      // Original app stores photos as { "0": {...}, "1": {...} } but feed expects array
      let photosArray: Array<{ url: string; aspectRatio?: number }> | undefined;
      if (post.photos) {
        if (Array.isArray(post.photos)) {
          // Already an array
          photosArray = post.photos;
        } else if (typeof post.photos === 'object') {
          // Convert Record to Array, sorting by numeric keys
          photosArray = Object.keys(post.photos)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map(key => (post.photos as any)[key])
            .filter(photo => photo && photo.url);
        }
      }

      // Combine feedItem data with post data exactly as the original app did
      return {
        ...post,
        id: itemId,
        key: post.key || itemId,
        // Ensure these critical fields from feedItem are included
        postType: feedItem.postType || post.type, // Use either postType or type
        type: feedItem.postType || post.type, // Include both for compatibility
        owner: feedItem.owner,
        dateCreated: feedItem.dateCreated,
        photos: photosArray, // Converted to array format
        // Make sure all content fields are preserved
        content: post.content || feedItem.content || '',
        note: post.note || feedItem.note || '',
        text: post.text || feedItem.text || '',
        title: post.title || feedItem.title || '',
        description: post.description || feedItem.description || '',
        // Social data from feed item
        likes: likes,
        likeCount: likeCount,
        likesCount: likeCount, // Add for backward compatibility
        comments: comments,
        commentCount: commentCount,
        commentsCount: commentCount, // Add for backward compatibility
        hasLiked: hasLiked,
        // Include original item reference
        itemRef: feedItem.itemRef,
        // Shadowban indicator for admin UI
        isShadowbanned: isShadowbanned,
        // Make sure author data is properly set with all fields needed
        author: {
          uid: feedItem.owner || post.owner || '',
          displayName: authorDisplayName,
          username: authorUsername,
          photoURL: authorPhotoURL,
          isInstructor: isInstructor
        }
      };
      })
      .filter(Boolean);

    // Return typed result
    return postsList as any[];
  }
);